import axiosClient from 'lib/axiosClient';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

const useDepartureWorkspaceMutations = () => {
  const { enqueueSnackbar } = useSnackbar();

  const request = useCallback(
    (promise, { successMessage } = {}) =>
      promise
        .then(res => {
          if (res.status === 200) {
            if (successMessage) {
              enqueueSnackbar(successMessage, { variant: 'success' });
            }
            return !!res.data.value ? res.data?.value : res.data;
          }
          enqueueSnackbar(res?.message, { variant: 'error' });
          return null;
        })
        .catch(err => {
          enqueueSnackbar(err?.message || err?.response?.data?.message, {
            variant: 'error',
          });
          return null;
        }),
    [enqueueSnackbar],
  );

  const autoInitAll = useCallback(
    operationGuid =>
      request(
        axiosClient.post('Operation/Departure/Task/AutoInitAll', {
          Guid: operationGuid,
        }),
        { successMessage: 'توزیع وظایف انجام شد' },
      ),
    [request],
  );

  const autoInit = useCallback(
    payload =>
      request(
        axiosClient.post('Operation/Departure/Task/AutoInit', {
          operationGuid: payload.operationGuid,
          operationProductGuid: payload.operationProductGuid,
          agentGuid: payload.agentGuid,
          count: payload.count,
          type: payload.type,
        }),
        { successMessage: 'برداشت اتوماتیک ثبت شد' },
      ),
    [request],
  );

  const initTask = useCallback(
    payload =>
      request(
        axiosClient.post('Operation/Departure/Task/Init', {
          operationGuid: payload.operationGuid,
          operationProductGuid: payload.operationProductGuid,
          addressGuid: payload.addressGuid,
          agentGuid: payload.agentGuid,
          count: payload.count,
        }),
        { successMessage: 'برداشت دستی ثبت شد' },
      ),
    [request],
  );

  const getAllLocality = useCallback(
    (zoneGuid, productPackGuid) =>
      request(
        axiosClient.get('Inventory/ProductData/GetAllLocality', {
          params: { Guid: zoneGuid, ProductPackGuid: productPackGuid },
        }),
      ),
    [request],
  );

  const getAvailableAgents = useCallback(
    zoneGuid =>
      request(
        axiosClient.get('InventoryAgent/GetAllAgentAvailable', {
          params: { Guid: zoneGuid },
        }),
      ),
    [request],
  );

  const insertAgent = useCallback(
    payload =>
      request(
        axiosClient.post('Operation/Departure/Agent/Insert', {
          operationGuid: payload.operationGuid,
          agentGuid: payload.agentGuid,
        }),
        { successMessage: 'PDA با موفقیت افزوده شد' },
      ),
    [request],
  );

  const deleteAgent = useCallback(
    operationAgentGuid =>
      request(
        axiosClient.delete('Operation/Departure/Agent/Delete', {
          params: { OperationAgentGUID: operationAgentGuid },
        }),
        { successMessage: 'PDA حذف شد' },
      ),
    [request],
  );

  const removeAgentTask = useCallback(
    agentTaskGuid =>
      request(
        axiosClient.delete('Operation/Departure/Agent/RemoveTask', {
          params: { GUID: agentTaskGuid },
        }),
        { successMessage: 'وظیفه از PDA حذف شد' },
      ),
    [request],
  );

  const getAgentDetail = useCallback(
    operationAgentGuid =>
      request(
        axiosClient.get('Operation/Departure/Workspace/Agent', {
          params: { Guid: operationAgentGuid },
        }),
      ),
    [request],
  );

  const runTask = useCallback(
    taskGuid =>
      request(
        axiosClient.put('Operation/Departure/Task/Run', null, {
          params: { GUID: taskGuid },
        }),
        { successMessage: 'وظیفه ارسال شد' },
      ),
    [request],
  );

  const runAllTasks = useCallback(
    (agentGuid, operationGuid) =>
      request(
        axiosClient.put('Operation/Departure/Task/RunAll', null, {
          params: { GUID: agentGuid, OperationGUID: operationGuid },
        }),
        { successMessage: 'همه وظایف ارسال شدند' },
      ),
    [request],
  );

  const getShoppingCartDetail = useCallback(
    shoppingCartGuid =>
      request(
        axiosClient.get('Operation/Departure/Workspace/ShoppingCart', {
          params: { Guid: shoppingCartGuid },
        }),
      ),
    [request],
  );

  const confirmMission = useCallback(
    taskGuid =>
      request(
        axiosClient.put('Operation/Departure/Task/MissionConfirm', null, {
          params: { TaskGuid: taskGuid },
        }),
        { successMessage: 'مأموریت تأیید شد' },
      ),
    [request],
  );

  const confirmAllMissions = useCallback(
    operationShoppingCartGuid =>
      request(
        axiosClient.put(
          'Operation/Departure/Task/ShoppingCartMissionsConfirm',
          null,
          {
            params: {
              OperationShoppingCartGuid: operationShoppingCartGuid,
            },
          },
        ),
        { successMessage: 'همه مأموریت‌های سبد تأیید شدند' },
      ),
    [request],
  );

  const submitOperation = useCallback(
    operationGuid =>
      request(
        axiosClient.put('Operation/Submit', null, {
          params: { GUID: operationGuid },
        }),
        { successMessage: 'عملیات با موفقیت اتمام شد' },
      ),
    [request],
  );

  const submitVoucher = useCallback(
    operationGuid =>
      request(
        axiosClient.put('Operation/Voucher/Submit', null, {
          params: { GUID: operationGuid },
        }),
        { successMessage: 'ثبت سند با موفقیت انجام شد' },
      ),
    [request],
  );

  return {
    autoInitAll,
    autoInit,
    initTask,
    getAllLocality,
    getAvailableAgents,
    insertAgent,
    deleteAgent,
    removeAgentTask,
    getAgentDetail,
    runTask,
    runAllTasks,
    getShoppingCartDetail,
    confirmMission,
    confirmAllMissions,
    submitOperation,
    submitVoucher,
  };
};

export default useDepartureWorkspaceMutations;
